import { computed } from 'vue';
import { interpret } from 'xstate';
import { useActor } from '@xstate/vue';

import { CurrentKey, LoanType, RelationshipType, APPLICATION_CONTEXT } from '@/helpers/const';
import { formatAddress } from '@/helpers/format';
import { includesEnum } from '@/helpers/includes-enum';
import { hasAssets } from '@/helpers/typeGuards';
import { deduplicateAddresses, hasAddressLine } from '@/helpers/useApplicationAddresses';
import { initMachine, loanApplicationMachineErhl, type ErhlLoanAppMachineContext } from '@/helpers/loanErhlAppMachine';
import { ErhlApplicationProcessSteps, ErhlProcessStep, ErhlProcessStepNames, StepStatus } from '@/types';

import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  ErhlApplicationProcessStepStatus,
  ErhlProcessStepEnum,
  LoanApplication,
  PropertyId,
} from '@/types';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const savedStateRaw = localStorage.getItem(appKey);
const savedState = savedStateRaw ? JSON.parse(savedStateRaw) : null;
const initialState: ErhlLoanAppMachineContext = savedState ? savedState : initMachine();

const service = interpret(loanApplicationMachineErhl.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as ErhlProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;
    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(ErhlApplicationProcessSteps).map(([step, info]) => ({
  id: step as ErhlProcessStepEnum,
  ...info,
}));

/**
 * Steps that require end debt > 0 to be shown
 * Note: Exit Strategy also requires Single Security loan type
 */
const DEBT_CONDITIONAL_STEPS: ErhlProcessStepEnum[] = [
  ErhlProcessStep.Income,
  ErhlProcessStep.Expenses,
  ErhlProcessStep.Assets,
];

/**
 * Checks if a step is one of the debt-conditional financial steps
 */
const isDebtConditionalStep = (stepId: ErhlProcessStepEnum): boolean => {
  return includesEnum(stepId, DEBT_CONDITIONAL_STEPS);
};

/**
 * Determines if financial steps should be shown based on loan conditions
 * @param loanApp - The loan application data
 * @param isSecondaryMarried - Whether secondary applicant is married
 * @param fromContext - The context from which application was initiated
 * @returns True if financial steps should be displayed
 */
const shouldShowFinancialSteps = (
  loanApp: LoanApplication,
  isSecondaryMarried: boolean,
  fromContext?: string,
): boolean => {
  // ERHL always collects income, expenses, and assets
  if (loanApp.lendingProduct === 'erhl') return true;

  // Always show for CRM
  if (fromContext === APPLICATION_CONTEXT.CRM) return true;

  // Show if has end debt and not secondary married
  if (loanApp.endDebt > 0 && !isSecondaryMarried) return true;

  return false;
};

/**
 * Checks if step should be visible in secondary declaration flow
 */
const isSecondaryDeclarationFlow = (loanApp: LoanApplication, authorisedUser: Record<string, string>): boolean => {
  return loanApp.userType === 'Secondary' && !authorisedUser.id && loanApp.actionType === 'updateExisting';
};

/**
 * Special case: Single Security loans always show Assets step
 */
const isSingleSecurityAssets = (stepId: ErhlProcessStepEnum, loanType?: string): boolean => {
  return loanType === LoanType.SINGLE_SECURITY && stepId === ErhlProcessStep.Assets;
};

/**
 * Determines if a step should be visible in the sidebar
 */
const shouldShowStep = (
  step: { id: ErhlProcessStepEnum; actions: string[] },
  loanApp: LoanApplication,
  isSecondaryMarried: boolean,
  authorisedUser: Record<string, string>,
  fromContext?: string,
): boolean => {
  // Always hide Finished step from sidebar
  if (step.id === ErhlProcessStep.Finished) return false;

  // Must have user type and matching action
  if (!loanApp.userType || !step.actions.includes(loanApp.actionType)) {
    return false;
  }

  // Exit Strategy is ONLY for Single Security loans
  if (step.id === ErhlProcessStep.ExitStrategy) {
    if (loanApp.lendingProduct === 'erhl') return true;
    return (
      loanApp.loanType === LoanType.SINGLE_SECURITY &&
      shouldShowFinancialSteps(loanApp, isSecondaryMarried, fromContext)
    );
  }

  // If not a debt-conditional step, always show
  if (!isDebtConditionalStep(step.id)) return true;

  // For debt-conditional steps, check conditions
  if (shouldShowFinancialSteps(loanApp, isSecondaryMarried, fromContext)) {
    return true;
  }

  // Secondary declaration flow shows financial steps
  if (isSecondaryDeclarationFlow(loanApp, authorisedUser)) {
    return true;
  }

  // Single Security always shows Assets
  if (isSingleSecurityAssets(step.id, loanApp.loanType)) {
    return true;
  }

  // For Assets step, only show if the loan application contains any assets
  if (step.id === ErhlProcessStep.Assets) {
    if (hasAssets(loanApp)) return true;
  }

  // Otherwise, hide debt-conditional steps
  return false;
};

/**
 * Gets the current valid step and its order from the process steps
 * @param currentStepId - The current step ID from state
 * @returns Object containing validated step and its order
 */
const getCurrentStepInfo = (currentStepId: ErhlProcessStepEnum) => {
  const validStep = ErhlProcessStepNames.includes(currentStepId) ? currentStepId : ErhlProcessStepNames[0];
  return {
    step: validStep,
    order: ErhlApplicationProcessSteps[validStep].order,
  };
};

/**
 * Calculates the display status for a step based on its current status and position
 */
const calculateStepStatus = (
  step: { id: ErhlProcessStepEnum },
  stepStatus: StepStatus,
  currentStepOrder: number,
): StepStatus => {
  if (stepStatus === StepStatus.Incomplete && ErhlApplicationProcessSteps[step.id].order < currentStepOrder) {
    return StepStatus.Skipped;
  }
  return stepStatus;
};

export function useErhlLoanAppProcess() {
  const { state, send } = useActor(service);
  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const authorisedUser = computed(() => state.value.context.authorisedUser);

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const stepper = computed<ErhlApplicationProcessStepStatus[]>(() => {
    const { order: currentStepOrder } = getCurrentStepInfo(state.value.context.current);
    return processSteps.reduce<ErhlApplicationProcessStepStatus[]>((visibleSteps, step) => {
      if (
        !shouldShowStep(step, loanApp.value, isSecondaryMarried.value, authorisedUser.value, state.value.context.from)
      ) {
        return visibleSteps;
      }

      const stepStatus = state.value.context.status[step.id];
      const status = calculateStepStatus(step, stepStatus, currentStepOrder);

      if (status === StepStatus.Skipped) {
        return visibleSteps;
      }

      visibleSteps.push({ ...step, status });
      return visibleSteps;
    }, []);
  });

  const loanProperties = computed(() =>
    [
      ...loanApp.value.incomingProperties,
      ...loanApp.value.outgoingProperties,
      ...(loanApp.value?.supportingProperties ?? []),
    ].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );

  /**
   * Addresses of the properties captured on this application's Property Details step.
   * Used to suggest addresses in the Borrower Details address fields.
   * `outgoingProperties` is excluded because ERHL always saves it empty.
   */
  const loanPropertyAddresses = computed<Address[]>(() =>
    deduplicateAddresses(
      [...(loanApp.value.incomingProperties ?? []), ...(loanApp.value.supportingProperties ?? [])]
        .map((property) => property.address as Address | undefined)
        .filter(hasAddressLine),
    ),
  );

  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updatedData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === ErhlProcessStep.SubmitApplication) {
      localStorage.setItem(CurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updatedData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = (brokerId?: string) => {
    if (brokerId) {
      send({ type: 'RESTART_BROKER', brokerId });
    } else {
      send({ type: 'RESTART' });
    }
  };

  return {
    state,
    service,
    stepper,
    currentStep,
    authorisedUser,
    loanApp,
    loanProperties,
    loanPropertyAddresses,
    loanApplicants,
    send,
    saveAndGoNext,
    goBack,
    restart,
  };
}
